/**
 * Logic-region entities (Area trigger, Camera zone, Point light,
 * Particle emitter) — split out of the old PlatformerEntities.js
 * monolith (which barrel-re-exports these). None of them block
 * movement; they react to the player's presence or feed renderers.
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { ParticleSystem, emitterConfigFromProps } from './ParticleSystem.js';
import { clampNum, parseHexColor } from './entityHelpers.js';

// ── Area / trigger zone (Godot Area2D analogue) ───────────────────
//
// A non-solid rectangular region the player can walk into. Unlike a
// solid body it never blocks movement; it just fires an `effect` when
// the player overlaps it. Effects map onto the scene's existing
// collision verdicts:
//   - hurt  → 'damage'  (continuous; the player's i-frames gate repeats,
//                         so it reads like lava / spikes)
//   - coin  → 'pickup'  (one-shot, then the zone consumes itself)
//   - grow  → 'powerup' (one-shot)
//   - win   → 'win'
//   - sound → 'sound'   (forwarded to the PlatformerNotes controller,
//                         which plays / loops the zone's audio clip
//                         once on enter — edge-triggered, see _fired)
//   - none  → marker only (reserved for future custom events)
// Invisible in-game by default (a logic zone); authors can flag it
// visible to ship a tinted hazard overlay. Always drawn in the editor.

const AREA_EFFECT_COLORS = {
    hurt: 0xef4444, coin: 0xfacc15, grow: 0x22c55e, win: 0xfbbf24,
    sound: 0x38bdf8, music: 0xa78bfa, none: 0x94a3b8,
};

export class AreaTriggerEntity {
    constructor(c, r, board, theme, props = {}) {
        const cs = board.cellSize;
        this.cell = { c, r };
        this.wCells = Math.max(1, Math.round(props.w || 2));
        this.hCells = Math.max(1, Math.round(props.h || 2));
        this.x = c * cs;
        this.y = r * cs;
        this.w = this.wCells * cs;
        this.h = this.hCells * cs;
        this.effect = props.effect || 'hurt';
        this.alive = true;
        this._fired = false;

        this.display = new Container();
        const visible = props.visible === 'visible' || props.visible === true;
        this.display.visible = visible;
        if (visible) {
            const color = AREA_EFFECT_COLORS[this.effect] ?? 0xa855f7;
            const g = new Graphics();
            g.rect(0, 0, this.w, this.h);
            g.fill({ color, alpha: 0.18 });
            g.rect(0, 0, this.w, this.h);
            g.stroke({ color, width: 2, alpha: 0.8 });
            this.display.addChild(g);
        }
        this.display.x = this.x;
        this.display.y = this.y;
    }

    update() { /* static detection zone — no per-frame motion */ }

    onCollidePlayer() {
        if (!this.alive) return null;
        switch (this.effect) {
            case 'win':
                this.alive = false;
                return 'win';
            case 'grow':
                if (this._fired) return null;
                this._fired = true; this.alive = false; this.display.visible = false;
                return 'powerup';
            case 'coin':
                if (this._fired) return null;
                this._fired = true; this.alive = false; this.display.visible = false;
                return 'pickup';
            case 'hurt':
                return 'damage';
            case 'sound':
                // Edge-triggered: the verdict fires once per ENTRY. The
                // scene's collision sweep clears `_inside` when the
                // player leaves the AABB (see _refreshAreaInsideState),
                // re-arming this zone for another play next time.
                if (this._inside) return null;
                this._inside = true;
                return 'sound';
            case 'music':
                // Same edge-trigger pattern as 'sound' — the verdict
                // fires once per ENTRY. The scene routes 'music' to
                // the notes controller, which interprets the zone's
                // `musicAction` prop (play / stop) to start, stop,
                // or crossfade the background music track.
                if (this._inside) return null;
                this._inside = true;
                return 'music';
            case 'none':
            default:
                return null;
        }
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

// ── Camera zone (scripted zoom moment) ────────────────────────────
//
// A non-solid rectangular region that drives the camera while the player
// is inside it: the camera eases to the zone's `zoom` and either keeps
// following the player ('player' focus) or locks onto the zone's centre
// ('zone' focus, for a framed cutscene-style shot). Leaving the zone
// releases the camera back to the stage's base follow zoom. Never blocks
// movement and never appears in-game (a pure logic region); the editor
// draws it as a labelled rectangle so it can be placed + sized.

export class CameraZoneEntity {
    constructor(c, r, board, theme, props = {}) {
        const cs = board.cellSize;
        this.cell = { c, r };
        this.wCells = Math.max(1, Math.round(props.w || 6));
        this.hCells = Math.max(1, Math.round(props.h || 4));
        this.x = c * cs;
        this.y = r * cs;
        this.w = this.wCells * cs;
        this.h = this.hCells * cs;
        this.zoom = clampNum(props.zoom, 0.25, 4, 1.6);
        this.focusMode = props.focus === 'zone' ? 'zone' : 'player';
        this.isCameraZone = true;
        this.alive = true;

        // World-space centre — the focus target for 'zone' mode.
        this.focusX = this.x + this.w / 2;
        this.focusY = this.y + this.h / 2;

        // A logic region: no in-game artwork. (The editor draws its own
        // placeholder; the runtime board skips painting camera zones.)
        this.display = new Container();
        this.display.visible = false;
    }

    /** True when a world point (the player centre) is inside the zone. */
    containsPoint(px, py) {
        return px >= this.x && px <= this.x + this.w
            && py >= this.y && py <= this.y + this.h;
    }

    update() { /* static region — no per-frame motion */ }
    onCollidePlayer() { return null; }      // never a collision verdict

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

// ── Point light (lighting) ────────────────────────────────────────
//
// A positional glow consumed by the runtime PlatformerLighting compositor.
// It owns no in-game artwork (the lightmap draws the glow); the editor paints
// a bulb + reach ring placeholder. Collected into the scene's `_lights` list
// (like camera zones) rather than ticked/collided as an enemy. `sample()`
// returns its live descriptor each frame, applying the optional flicker.

export class PointLightEntity {
    constructor(c, r, board, theme, props = {}) {
        const cs = board.cellSize;
        this.cell = { c, r };
        // World-space centre — the light origin the compositor projects to
        // screen space each frame.
        this.x = c * cs + cs / 2;
        this.y = r * cs + cs / 2;
        this.w = cs;
        this.h = cs;
        this.alive = true;
        this.isLight = true;

        this.color = parseHexColor(props.color, 0xffd9a0);
        this.radius = clampNum(props.radius, 8, 2000, 160);
        this.intensity = clampNum(props.intensity, 0, 4, 1.1);
        this.flicker = clampNum(props.flicker, 0, 1, 0);
        this._t = Math.random() * 1000;   // desync flicker between lights

        // Lighting-only: no in-game render node.
        this.display = new Container();
        this.display.visible = false;
    }

    /**
     * Live light descriptor for the compositor. `dt` (seconds) advances the
     * flicker clock; the returned intensity wobbles when `flicker > 0` (torch
     * / fire feel) and is otherwise the steady configured value.
     */
    sample(dt = 0) {
        let inten = this.intensity;
        if (this.flicker > 0) {
            this._t += dt;
            // Two detuned sines → an organic, non-repeating shimmer.
            const w = 0.5 + 0.5 * Math.sin(this._t * 11.3) * Math.sin(this._t * 4.7 + 1.3);
            inten *= Math.max(0, 1 - this.flicker * (1 - w));
        }
        return { x: this.x, y: this.y, radius: this.radius, color: this.color, intensity: inten };
    }

    update() { /* static light — no per-frame motion */ }
    onCollidePlayer() { return null; }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

// ── Particle emitter (fx) ─────────────────────────────────────────
//
// A purely visual entity that runs a ParticleSystem at its cell. Never
// collides (default collision layer = Decoration, which the player's
// mask ignores) and never blocks anything — it just emits. The runtime
// ticks it every frame; the editor renders a live preview the same way.

export class ParticleEmitterEntity {
    constructor(c, r, board, theme, props = {}) {
        const cs = board.cellSize;
        this.cell = { c, r };
        // Origin = centre of the cell, in world coordinates. The display
        // container sits at (0,0); particles carry absolute world coords
        // so they pan correctly with the camera.
        this.x = c * cs + cs / 2;
        this.y = r * cs + cs / 2;
        this.w = cs;
        this.h = cs;
        this.alive = true;
        this._props = props || {};
        this._board = board;
        this._bodies = null;            // player-body getter (set by scene)
        this._wasInside = false;        // edge-detect for contact triggers

        this.display = new Container();
        this._sys = new ParticleSystem(this.display, emitterConfigFromProps(props));
        this._sys.setOrigin(this.x, this.y);
        // Give the system the board's tile grid so emitters that opt into
        // collision (props.collide) can land particles on the terrain.
        // Cheap O(1) cell lookups; a no-op for the default 'off' emitter.
        this._sys.setColliders({
            cellSize: board.cellSize,
            solidAt: (c2, r2) => board.isSolidAt(c2, r2),
            oneWayAt: typeof board.isOneWayAt === 'function' ? (c2, r2) => board.isOneWayAt(c2, r2) : null,
        });
        // PNG-particle mode: resolve the picked slot to a Texture (the
        // board caches src→Texture) and feed it to the system.
        if (this._props.texture) {
            const src = theme?.get?.(this._props.texture);
            if (src && typeof board._loadBgTexture === 'function') {
                board._loadBgTexture(src, (tex) => this._sys.setTexture(tex));
            }
        }
    }

    /**
     * Let the scene supply the live dynamic bodies (the player). Used
     * both to bounce particles off the player ('ground+player' collision)
     * AND to drive proximity / contact emission triggers. The getter is
     * re-read each frame, so it survives respawns.
     */
    setCollisionBodies(getBodies) {
        this._bodies = typeof getBodies === 'function' ? getBodies : null;
        this._sys.setBodyProvider(getBodies);
    }

    /**
     * Gate emission for the "action" trigger before stepping the system:
     *   - continuous → always emit (default).
     *   - proximity  → emit only while the player is within the radius
     *                  (e.g. leaves falling as the player passes a tree).
     *   - contact    → one-shot burst each time the player ENTERS the
     *                  radius (e.g. dust when landing on a platform).
     * No player (or no getter, as in the editor) → continuous still emits;
     * the other modes simply stay idle until a player shows up.
     */
    _applyTrigger() {
        const mode = this._props.emit || 'continuous';
        if (mode === 'continuous') { this._sys.setEmitting(true); return; }

        const player = this._bodies?.()?.[0];
        if (!player) { this._sys.setEmitting(false); this._wasInside = false; return; }

        const px = player.x + player.w / 2;
        const py = player.y + player.h / 2;
        const dx = px - this.x, dy = py - this.y;
        const radius = Number.isFinite(this._props.triggerRadius) ? this._props.triggerRadius : 96;
        const inside = (dx * dx + dy * dy) <= radius * radius;

        if (mode === 'proximity') {
            this._sys.setEmitting(inside);
        } else {                              // contact: edge-triggered burst
            this._sys.setEmitting(false);
            if (inside && !this._wasInside) {
                const n = Number.isFinite(this._props.burst) ? this._props.burst : 16;
                this._sys.burst(n);
            }
        }
        this._wasInside = inside;
    }

    update() {
        if (!this.alive) return;
        // Resolve the emission trigger, then step at a fixed 60 Hz —
        // matches the platformer's locked timestep.
        this._applyTrigger();
        this._sys.update(1 / 60);
    }

    onCollidePlayer() { return null; }

    destroy() {
        this._sys?.destroy();
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}
